import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import bp from 'styled-components-breakpoint';
import { getViewport } from '../../utilities/ViewportHelper/viewportHelper';
import { ShortSecondaryButton } from '../Button/button.styled';
import { ChevronDown } from '../Icons';
import Link from '../Link';
import theme from '../../themes';

const StyledButton = styled(ShortSecondaryButton)`
  ${(props) => props.theme.typography.bodySmall}
  background-color: transparent;
  display: none;
  padding: 0;
  width: 2.5rem;
  border: 2px solid ${(props) => props.theme.colors.grayscale.white};
  color: ${(props) => props.theme.colors.grayscale.white};
  ${bp('desktopLarge')`
    display: inline-block;
  `};
`;

StyledButton.displayName = 'StyledButton';

const StyledAccountButton = styled.a`
  ${(props) => props.theme.typography.button}
  background-color: transparent;
  border: 0;
  cursor: pointer;
  display: none;
  position: relative;
  text-decoration: none;
  color: ${(props) => props.theme.colors.grayscale.white};
  width: auto;
  ${bp('tablet')`
    align-items: center;
    display: flex;
    &:hover:enabled {
      background-color: transparent;
      color: ${(props) => props.theme.colors.grayscale.white};
    }

    &:focus:enabled,
    &:active:enabled {
      background-color: transparent;
      border: 0;
      box-shadow: none;
      color: ${(props) => props.theme.colors.grayscale.white};
      outline: 0;
    }
    &:[aria-label='close menu'] {
      background-color: ${(props) => props.theme.colors.grayscale.gray4};
      color: ${(props) => props.theme.colors.grayscale.black};
    }
  `};
  ${bp('desktopLarge')`
    display: none;
  `};

  &:hover {
    & svg {
      & path {
        fill: ${(props) => props.theme.colors.grayscale.black};
      }
    }
  }

  svg {
    transform: ${(props) =>
      props['data-isopen'] ? 'rotate(180deg)' : 'rotate(0)'};
    transition: transform 0.2s ease-in;
    fill: ${(props) =>
      props['data-isopen']
        ? props.theme.colors.grayscale.black
        : props.theme.colors.grayscale.gray0};
  }

  #userbadge-label {
    color: ${(props) => props.theme.colors.grayscale.white};

    :hover,
    :enabled,
    :active {
      color: ${(props) => props.theme.colors.grayscale.black};
    }
  }
`;

StyledAccountButton.displayName = 'StyledAccountButton';

const StyledLink = styled(Link)`
  ${(props) => props.theme.typography.bodySmall};
  align-self: center;
  color: ${(props) => props.theme.colors.grayscale.white};
  text-align: right;
  width: auto;
  ${bp('desktopLarge')`
    margin-right: 2rem;
  `};
`;

const StyledBadge = styled.div`
  display: inline-block;
  ${bp('desktopLarge')`
    padding: 0 1rem;
  `};
  position: relative;

  a {
    color: ${(props) => props.theme.colors.grayscale.black};
  }

  & svg {
    & path {
      fill: {
        color: ${(props) => props.theme.colors.grayscale.gray0};
      }
    }
  }

  &[aria-expanded='true'] {
    ${StyledButton} {
      background-color: ${(props) => props.theme.colors.grayscale.black};
      color: ${(props) => props.theme.colors.grayscale.white};
    }
  }
`;

const StyledMenu = styled.ul`
  ${(props) => props.theme.typography.body};
  background-color: ${(props) => props.theme.colors.grayscale.white};
  border-radius: 0;
  box-shadow: 1px 1px 4px 1px rgb(0 0 0 / 15%);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;
  position: absolute;
  right: 0;
  top: 41px;
  width: 208px;
  z-index: ${(props) => props.theme.zIndex.menubox};
  ${bp('tablet')`
    top: 40px;
  `};
  ${bp('desktop')`
    top: 41px;
  `};
`;

const StyledListEntry = styled.li`
  a {
    color: ${(props) => props.theme.colors.grayscale.gray0};
    ${(props) => props.theme.typography.body};
    display: block;
    padding: 0.5rem 0.5rem 0.5rem 1rem;

    :hover,
    :focus {
      color: ${(props) => props.theme.colors.grayscale.black};
      background-color: ${(props) => props.theme.colors.yellow.primary};
    }
  }

  &[aria-selected='true'] {
    a {
      background-color: ${(props) => props.theme.colors.yellow.primary};
      color: ${(props) => props.theme.colors.grayscale.black};
    }
  }

  :nth-child(2) {
    a {
      margin-top: 0.5rem;
    }
  }

  :last-child {
    a {
      margin-bottom: 0.5rem;
    }
  }
`;

const StyledFirstItem = styled(StyledListEntry)`
  border-bottom: 1px solid ${(props) => props.theme.colors.grayscale.gray3};

  a {
    ${(props) => props.theme.typography.body};
  }
`;

/**
 * Click event handler for the items.
 *
 * @param {Object} event - click event
 */
function handleClick(event) {
  // prevents default navigation behavior to allow handling everything with onChange
  /* istanbul ignore next */
  event.preventDefault();
}

function renderDropDown(
  initials,
  profileUrl,
  firstName,
  urlList,
  controlledIsOpen,
  logOutUrl,
  viewport,
  linkWrapper,
  onChange,
  otherProps
) {
  return (
    <Downshift
      id="userbadge"
      isOpen={controlledIsOpen}
      onChange={onChange}
      {...otherProps}
    >
      {({
        getItemProps,
        getLabelProps,
        getMenuProps,
        isOpen,
        getRootProps,
        getToggleButtonProps,
      }) => {
        const buttonDisplay = (
          <>
            <StyledButton
              id="userbadge-button"
              type="button"
              {...getToggleButtonProps()}
            >
              <span {...getLabelProps()}>{initials}</span>
            </StyledButton>
            <StyledAccountButton
              id="userbadge-myaccount"
              data-isopen={isOpen}
              type="button"
              {...getToggleButtonProps()}
            >
              <span {...getLabelProps()}>My Account</span>
              <ChevronDown />
            </StyledAccountButton>
          </>
        );

        return (
          <StyledBadge {...getRootProps()}>
            {buttonDisplay}
            <StyledMenu {...getMenuProps()} isOpen={isOpen} theme={theme}>
              {isOpen ? (
                <>
                  <StyledFirstItem
                    {...getItemProps({
                      key: profileUrl,
                      item: { url: profileUrl, text: 'User Profile' },
                    })}
                    theme={theme}
                  >
                    <Link
                      id="header-userbadge-profile"
                      href={profileUrl}
                      LinkWrapper={linkWrapper}
                      onClick={handleClick}
                    >
                      {`Hi, ${firstName}!`}
                    </Link>
                  </StyledFirstItem>
                  {urlList.map((item) => (
                    <StyledListEntry
                      {...getItemProps({
                        key: item.text,
                        item,
                      })}
                      theme={theme}
                    >
                      <Link
                        id={`header-userbadge-${item.text.replace(/ /, '-')}`}
                        href={item.url}
                        LinkWrapper={linkWrapper}
                        onClick={handleClick}
                      >
                        {item.text}
                      </Link>
                    </StyledListEntry>
                  ))}
                  <StyledListEntry
                    {...getItemProps({
                      key: logOutUrl,
                      item: { url: logOutUrl, text: 'Log Out' },
                    })}
                    theme={theme}
                  >
                    <Link
                      id="header-userbadge-logout"
                      href={logOutUrl}
                      LinkWrapper={linkWrapper}
                      onClick={handleClick}
                    >
                      <span>Log Out</span>
                    </Link>
                  </StyledListEntry>
                </>
              ) : null}
            </StyledMenu>
          </StyledBadge>
        );
      }}
    </Downshift>
  );
}
/**
 * Renders a user badge with the user initials or a sign-in
 * link if there is no user data.
 */
function UserBadge({
  user,
  profileUrl,
  logInUrl,
  logOutUrl,
  urlList,
  controlledIsOpen,
  linkWrapper,
  onChange,
  ...rest
}) {
  const { id, firstName, lastName } = user || {};
  const [viewport, setViewport] = useState('');
  let initials = '';

  const handleResize = (event) => {
    const newViewport = getViewport(event.target.innerWidth);
    setViewport(newViewport);
    /* Release the scroll when the viewport in not mobile */
    if (newViewport !== 'mobile') {
      const applicationNode = document.getElementsByTagName('body')[0];
      applicationNode.style.overflow = 'visible';
    }
  };

  useEffect(() => {
    // sets viewport before resize
    setViewport(getViewport(window.innerWidth));
    window.addEventListener('resize', handleResize);

    // remove the event listener in the cleanup phase
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  if (typeof firstName !== 'string' || typeof lastName !== 'string') {
    if (user) console.error('Invalid user data in UserBadge component:', user);
  } else {
    initials = `${firstName[0]}${lastName[0]}`;
  }
  return id ? (
    renderDropDown(
      initials,
      profileUrl,
      firstName,
      urlList,
      controlledIsOpen,
      logOutUrl,
      viewport,
      linkWrapper,
      onChange,
      rest
    )
  ) : (
    <StyledLink
      id="header-login"
      as={Link}
      href={logInUrl}
      LinkWrapper={linkWrapper}
    >
      Sign in
    </StyledLink>
  );
}

/**
 * propTypes for this component
 */
UserBadge.propTypes = {
  /**
   *  User object
   */
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    id: PropTypes.string,
  }),
  /**
   *  Profile link URL
   */
  profileUrl: PropTypes.string,
  /**
   *  Log In link URL
   */
  logInUrl: PropTypes.string,
  /**
   *  Log Out link URL
   */
  logOutUrl: PropTypes.string,
  /**
   *  List of URLs to fill the drop-down.
   */
  urlList: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  /**
   *  controlled prop for isOpen state of drop-down. Mostly for testing purposes.
   */
  controlledIsOpen: PropTypes.bool,
  /**
   *  link wrapper for the Link component
   */
  linkWrapper: PropTypes.func,
  /**
   *  onChange event handler for DropDown.
   */
  onChange: PropTypes.func,
};

/**
 * Default propTypes
 */
UserBadge.defaultProps = {
  user: null,
  profileUrl: '/profile',
  logInUrl: '/signin',
  logOutUrl: '/logout',
  controlledIsOpen: undefined,
  linkWrapper: null,
  onChange: ({ url }) => {
    window.location.href = url;
  },
};

/* @component */
export default UserBadge;
